/**
 * What a unit has decided about photographs — TROOP-225, §6.
 *
 * **Two settings, asked in that order.** `photosEnabled` is whether there are photographs at all;
 * `uploadAudience` is who fills them, and it says nothing while the switch is off. The screen
 * behind these renders the second only while the first is on, for the same reason the API declines
 * to make the audience mean anything then.
 *
 * **`/units/` only, and deliberately no `/family` twin.** This is a write over the unit's own
 * record, which §3 puts outside guardian standing entirely. What a parent needs to know about the
 * setting reaches them where it matters — the gallery panel tells them whether they may add, and
 * who does.
 */

import { API_BASE_URL, ApiError } from './client';
import type { PhotoUploadAudience } from '../lib/photoAudience';

export interface UnitPhotoSettings {
  photosEnabled: boolean;
  uploadAudience: PhotoUploadAudience;
  /**
   * Whether this reader may change either of them — adult officers and admins. The server refuses
   * the write regardless of what the screen draws; this is only what renders.
   */
  canChange: boolean;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return (await response.json()) as T;
}

export const unitPhotoSettingsApi = {
  get: (unitId: string) => call<UnitPhotoSettings>(`/units/${unitId}/photo-settings/`),

  /**
   * Each field is optional and an absent one is left alone — the switch and the audience are two
   * controls, and writing one must not reset the other.
   */
  set: (
    unitId: string,
    change: { photosEnabled?: boolean; uploadAudience?: PhotoUploadAudience },
  ) =>
    call<UnitPhotoSettings>(`/units/${unitId}/photo-settings/`, {
      method: 'PUT',
      body: JSON.stringify(change),
    }),
};
